import { observer } from "mobx-react";
import { useStores } from "../../stores/root-store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faClock,
  faCutlery,
  faMapMarker,
  faMotorcycle,
  faPersonWalking,
} from "@fortawesome/free-solid-svg-icons";
import { ChangeEvent, useState, useMemo, useEffect } from "react";
import { IOrderTypeDetails } from "../../stores/order/index.d";
import { DeliveryMethod } from "../../stores/index.d";
import NavBar from "../../shared/navbar";
import ShopClosedNotice from "../../shared/shop-closed-notice";
import {
  currencyFormatter,
  formatDate,
  getAsapTime,
  getShopFutureDates,
  getShopWorkingHours,
  ShowAlert,
} from "../../utils/functions";
import { useShopContext } from "../../contexts/shop-context";
import { useNavigate } from "react-router-dom";
import { IDistanceCheckResponse } from "../../stores/cart";

const HomePage = observer(() => {
  const {
    isShopClosed,
    shopStartHour,
    shopEndHour,
    shopOpenHoursInterval,
    allowFutureDateOrders,
    noOfFutureDaysAllowed,
    estimatedTimeForCollection,
    estimatedTimeForDelivery,
    weeklyShopClosureDays,
    orderDeliveryRadius,
    shopPostcode,
    minimumOrderAmount,
  } = useShopContext();
  const { cartStore } = useStores();
  const { storeOrderTypeDetails, checkDistance } = cartStore;
  const navigate = useNavigate();
  const [orderTypeDetails, setOrderTypeDetails] = useState<IOrderTypeDetails>({
    deliveryMethod: DeliveryMethod.Delivery,
    postcode: "",
    date: formatDate(new Date(), "YYYY-MM-DD"),
    time: "0",
    isPostcodeDeliverable: false,
  });

  useEffect(() => {
    if (orderTypeDetails?.deliveryMethod)
      setOrderTypeDetails((prev: IOrderTypeDetails) => ({
        ...prev,
        ["time"]: getAsapTime(
          orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery
            ? estimatedTimeForDelivery
            : estimatedTimeForCollection
        ),
      }));
  }, [orderTypeDetails?.deliveryMethod]);

  useEffect(() => {
    storeOrderTypeDetails(null);
  }, [isShopClosed]);

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    setOrderTypeDetails((prev: IOrderTypeDetails) => ({
      ...prev,
      [name]: value,
    }));

    if (name == "postcode")
      setOrderTypeDetails((prev: IOrderTypeDetails) => ({
        ...prev,
        ["isPostcodeDeliverable"]: false,
      }));
  };

  const handleSelectChange = (evt: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = evt.currentTarget;
    setOrderTypeDetails((prev: IOrderTypeDetails) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOrderNowClick = () => {
    if (orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery) {
      if (!orderTypeDetails.postcode) {
        ShowAlert.Error("Please enter valid postcode.");
        return;
      }

      if (!orderTypeDetails.isPostcodeDeliverable) {
        ShowAlert.Error(
          "Sorry! Postcode out of our service area. Try collection."
        );
        return;
      }
    }

    if (!orderTypeDetails.time) {
      ShowAlert.Error("Sorry! Service not avilable.");
      return;
    }

    storeOrderTypeDetails(orderTypeDetails);
    navigate("prods");
  };

  const handlePostCodeApply = () => {
    if (orderTypeDetails.postcode)
      checkDistance(
        shopPostcode,
        orderTypeDetails.postcode,
        checkDistanceCallback
      );
  };

  const checkDistanceCallback = (dist: IDistanceCheckResponse) => {
    if (!dist) ShowAlert.Error("Please enter valid postcode.");
    else if (dist.distanceInMiles > orderDeliveryRadius)
      ShowAlert.Error(
        "You are outside our service area. Please try collection."
      );
    else
      setOrderTypeDetails((prev: IOrderTypeDetails) => ({
        ...prev,
        ["isPostcodeDeliverable"]: true,
      }));
  };

  const dates = useMemo(
    () =>
      getShopFutureDates(
        noOfFutureDaysAllowed,
        allowFutureDateOrders,
        weeklyShopClosureDays
      ),
    [noOfFutureDaysAllowed, allowFutureDateOrders, weeklyShopClosureDays]
  );

  const openHours = useMemo(() => {
    return getShopWorkingHours(
      shopStartHour,
      shopEndHour,
      shopOpenHoursInterval,
      orderTypeDetails.date,
      orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery
        ? estimatedTimeForDelivery
        : estimatedTimeForCollection
    );
  }, [
    shopStartHour,
    shopEndHour,
    shopOpenHoursInterval,
    orderTypeDetails,
    estimatedTimeForDelivery,
    estimatedTimeForCollection,
  ]);

  return (
    <section className="home-hero bg-cover position-relative">
      <NavBar bgClass="bg-default-transparent" />
      <div className="home-order-stage">
        <div
          className={`card home-order-card${isShopClosed ? " home-order-card--closed" : ""}`}
          aria-labelledby="home-order-title"
        >
          <div className="home-order-content">
            <header className="home-order-heading">
              <p className="home-order-eyebrow">Fresh from Noshe</p>
              <h1 id="home-order-title">
                {isShopClosed ? "Something delicious awaits." : "How would you like to order?"}
              </h1>
              <p>
                {isShopClosed
                  ? "A little inspiration for your next meal."
                  : "Your favourites, delivered or ready to collect."}
              </p>
            </header>
            {isShopClosed && <ShopClosedNotice />}
            <fieldset className="home-order-methods">
              <legend className="visually-hidden">Order type</legend>
              <input
                type="radio"
                className="btn-check"
                name="deliveryMethod"
                id="deliveryType1"
                autoComplete="off"
                value={DeliveryMethod.Delivery}
                checked={
                  orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery
                }
                onChange={handleInputChange}
              />
              <label
                className="btn home-order-method"
                htmlFor="deliveryType1"
              >
                <FontAwesomeIcon icon={faMotorcycle} className="me-2" />
                Delivery
              </label>
              <input
                type="radio"
                className="btn-check"
                name="deliveryMethod"
                id="deliveryType2"
                autoComplete="off"
                value={DeliveryMethod.Collection}
                checked={
                  orderTypeDetails.deliveryMethod == DeliveryMethod.Collection
                }
                onChange={handleInputChange}
              />
              <label
                className="btn home-order-method"
                htmlFor="deliveryType2"
              >
                <FontAwesomeIcon icon={faPersonWalking} className="me-2" />
                Collection
              </label>
            </fieldset>
            {orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery && (
              <>
                <div className="input-group home-order-field">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faMapMarker} />
                  </span>
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="postcode"
                      name="postcode"
                      placeholder="Postcode"
                      autoComplete="postal-code"
                      value={orderTypeDetails.postcode}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="postcode">Delivery postcode</label>
                  </div>
                  <button
                    type="button"
                    className="btn btn-default home-order-apply"
                    onClick={handlePostCodeApply}
                  >
                    Apply
                  </button>
                </div>
              </>
            )}
            {(orderTypeDetails.deliveryMethod == DeliveryMethod.Collection ||
              (orderTypeDetails.postcode &&
                orderTypeDetails.isPostcodeDeliverable)) &&
              allowFutureDateOrders && (
                <div className="input-group home-order-field">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faCalendarDay} />
                  </span>
                  <div className="form-floating">
                    <select
                      className="form-control"
                      id="date"
                      name="date"
                      value={orderTypeDetails.date}
                      onChange={handleSelectChange}
                    >
                      {dates?.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.text}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="date">Date</label>
                  </div>
                </div>
              )}
            {((orderTypeDetails.postcode &&
              orderTypeDetails.isPostcodeDeliverable &&
              orderTypeDetails.date) ||
              orderTypeDetails.deliveryMethod == DeliveryMethod.Collection) && (
              <div className="input-group home-order-field">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faClock} />
                </span>
                <div className="form-floating">
                  <select
                    className="form-control"
                    id="time"
                    name="time"
                    value={orderTypeDetails.time}
                    onChange={handleSelectChange}
                  >
                    {openHours?.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.text}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="time">Order time</label>
                </div>
              </div>
            )}
            {(orderTypeDetails.deliveryMethod == DeliveryMethod.Collection ||
              orderTypeDetails.isPostcodeDeliverable) && (
              <button
                type="button"
                className="btn btn-default home-order-action"
                onClick={handleOrderNowClick}
                disabled={isShopClosed}
              >
                Order Now
              </button>
            )}

            <a
              href="/prods"
              className={`btn home-order-action ${
                !isShopClosed &&
                (orderTypeDetails.deliveryMethod == DeliveryMethod.Collection ||
                  orderTypeDetails.isPostcodeDeliverable)
                  ? "home-order-browse-secondary"
                  : "btn-skip-browse-menu"
              }`}
            >
              <FontAwesomeIcon icon={faCutlery} />
              <span>Skip & Browse Menu</span>
            </a>
            {orderTypeDetails.deliveryMethod == DeliveryMethod.Delivery && (
              <p className="home-order-minimum">
                Minimum delivery order{" "}
                <strong>{currencyFormatter(minimumOrderAmount)}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

export default HomePage;
